import { queryOptions, useQuery } from '@tanstack/react-query';

import { GlyTouCan_API } from '@/config/endpoints';
import { api } from '@/lib/glytoucan-api-client';
import { QueryConfig } from '@/lib/react-query';
import { UserProfileSchema } from '@/types/api';
import { validate } from '@/utils/validation';

export const getUserProfile = async () => {
  return api
    .get(GlyTouCan_API.USER_PROFILE)
    .then(response => {
      return validate(
        response.data,
        UserProfileSchema,
        response.request?.responseURL,
      );
    })
    .catch(e => {
      if (e.errorType === 'AUTHORIZATION_ERROR') {
        return { contents: false } as const;
      }
      throw e;
    });
};

export const getUserProfileQueryOptions = () => {
  return queryOptions({
    queryKey: ['user-profile'],
    queryFn: () => getUserProfile(),
    // if the user is not logged in, the API returns { contents: false }
    // Polling -- re-fetch this API every 15 seconds.
    // 5000ms = 5sec
    staleTime: 1000 * 5,
    // 15000ms = 15sec
    refetchInterval: query => {
      // NOTE: Once the fetch results in a 401 error (data.contents === false),
      //       automatic re-fetching is disabled because the user is logged out.
      if (query.state.data && query.state.data.contents === false) {
        return false;
      }
      return 1000 * 15;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
};

type UseUserProfileOptions = {
  queryConfig?: QueryConfig<typeof getUserProfileQueryOptions>;
};

export const useUserProfile = ({ queryConfig }: UseUserProfileOptions) => {
  return useQuery({
    ...getUserProfileQueryOptions(),
    ...queryConfig,
  });
};
